import { z } from 'zod';

export const dataClassificationSchema = z.enum([
  'public',
  'product',
  'member_entered',
  'clinical',
  'credential',
]);

export type DataClassification = z.infer<typeof dataClassificationSchema>;

export const replayElementMetadataSchema = z
  .object({ data_classification: dataClassificationSchema })
  .strict();

export type ReplayElementMetadata = z.infer<typeof replayElementMetadataSchema>;

export const replayMetadataSchema = z
  .object({ elements: z.record(z.string(), replayElementMetadataSchema) })
  .strict();

export type ReplayMetadata = z.infer<typeof replayMetadataSchema>;
export type ReplayPrivacyEnvironment = 'development' | 'production';
export type ReplayPrivacyPolicy = 'record' | 'mask' | 'block';
export type ReplayAutocapturePolicy = 'allowed' | 'semantic-label-only' | 'suppressed';

const POLICY_RANK: Record<ReplayPrivacyPolicy, number> = {
  record: 0,
  mask: 1,
  block: 2,
};

function classificationPolicy(
  environment: ReplayPrivacyEnvironment,
  classification?: DataClassification,
): ReplayPrivacyPolicy {
  if (!classification || classification === 'credential') return 'block';
  if (
    environment === 'production' &&
    (classification === 'member_entered' || classification === 'clinical')
  ) {
    return 'mask';
  }
  return 'record';
}

/** Resolve remote metadata without allowing it to weaken a client-owned floor. */
export function resolveReplayPrivacyPolicy(input: {
  environment: ReplayPrivacyEnvironment;
  dataClassification?: DataClassification;
  componentFloor?: ReplayPrivacyPolicy;
  environmentFloor?: ReplayPrivacyPolicy;
}): ReplayPrivacyPolicy {
  const policies: ReplayPrivacyPolicy[] = [
    classificationPolicy(input.environment, input.dataClassification),
    input.componentFloor ?? 'record',
    input.environmentFloor ?? 'record',
  ];
  return policies.reduce((effective, candidate) =>
    POLICY_RANK[candidate] > POLICY_RANK[effective] ? candidate : effective,
  );
}

/** Resolve the independent autocapture decision frozen by ADR-080. */
export function resolveReplayAutocapturePolicy(input: {
  dataClassification?: DataClassification;
  componentFloor?: ReplayPrivacyPolicy;
  environmentFloor?: ReplayPrivacyPolicy;
}): ReplayAutocapturePolicy {
  if (
    !input.dataClassification ||
    input.dataClassification === 'credential' ||
    input.componentFloor === 'block' ||
    input.environmentFloor === 'block'
  ) {
    return 'suppressed';
  }
  if (input.dataClassification === 'member_entered' || input.dataClassification === 'clinical') {
    return 'semantic-label-only';
  }
  return 'allowed';
}
